import type { TFunction } from 'i18next';
import type { UndeliveredAttributionRow } from './types';

/**
 * v2 未妥投归因（2026-09-15 数仓落库 + 20260908 尾程新指标）。
 *
 * 口径与 `wms/dashboard/delivery/UndeliveredAttributionPage` 完全对齐：
 * - warehouseSideLcCount 已含 otherLcCount，前端不重复相加（[[project_warehouse_side_lc_includes_other]]）
 * - 未妥投总数 = driverSide + warehouseSide + handoverDiff + return
 *
 * 表内 12 子列由 4 大类分组，与 PRD 一致：司机侧 3 / 仓库侧 7 / 交接差异 1 / 退件 1。
 *
 * i18n：标签统一以 `labelKey` 存 i18n path，展示时通过 `getLevel1Label` / `getLevel2Label` 用 `t()` 解析，
 * 避免硬编码中文出现在英文/西班牙文环境。
 */

/** 一级归因枚举 name()，与 swiftx-dashboard 的 SlaUndeliveredReasonLevel1 对齐 */
export type UndeliveredLevel1 =
    | 'DRIVER_SIDE'
    | 'WAREHOUSE_SIDE'
    | 'HANDOVER_DIFF'
    | 'RETURN';

/** 二级归因枚举 name()，与 swiftx-dashboard 的 SlaUndeliveredReasonLevel2 对齐 */
export type UndeliveredLevel2 =
    | 'OUT_FOR_DELIVERY_NODE'
    | 'FAILED_NOT_RETURNED_NODE'
    | 'LOST_DAMAGED_DRIVER'
    | 'RETURN_WRONG_UNSCAN_NODE'
    | 'NOT_PICKED_UP'
    | 'RETURN_NOT_PUT_AWAY'
    | 'RETURN_ON_PUT_AWAY'
    | 'RETURN_UN_PUT_AWAY'
    | 'LOST_DAMAGED_WHS'
    | 'OTHER'
    | 'HANDOVER_DIFF_NODE'
    | 'RETURN_PKG';

/** 一级大类展示：颜色 + i18n labelKey（复用现有 columnGroups 三语） */
export const LEVEL1_META: Record<UndeliveredLevel1, { labelKey: string; color: string; totalField: keyof UndeliveredAttributionRow }> = {
    DRIVER_SIDE: { labelKey: 'wms.delivery.parcelSlaMonitoring.columnGroups.driverSide', color: '#3B5FE5', totalField: 'driverSideLcCount' },
    WAREHOUSE_SIDE: { labelKey: 'wms.delivery.parcelSlaMonitoring.columnGroups.warehouseSide', color: '#7C5CE0', totalField: 'warehouseSideLcCount' },
    HANDOVER_DIFF: { labelKey: 'wms.delivery.parcelSlaMonitoring.columnGroups.handoverDiff', color: '#E88A2A', totalField: 'handoverDiffLcCount' },
    RETURN: { labelKey: 'wms.delivery.parcelSlaMonitoring.columnGroups.return', color: '#8A8F99', totalField: 'returnLcCount' },
};

/** 二级明细：i18n labelKey + 归属行字段（用于渲染表格数值 + 未妥投总数求和） */
export const LEVEL2_META: Record<UndeliveredLevel2, { labelKey: string; field: keyof UndeliveredAttributionRow; level1: UndeliveredLevel1 }> = {
    OUT_FOR_DELIVERY_NODE: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.outForDeliveryNode', field: 'outForDeliveryNodeLcCount', level1: 'DRIVER_SIDE' },
    FAILED_NOT_RETURNED_NODE: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.failedNotReturnedNode', field: 'failedNotReturnedNodeLcCount', level1: 'DRIVER_SIDE' },
    LOST_DAMAGED_DRIVER: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.lostDamagedDriver', field: 'lostDamagedDriverLcCount', level1: 'DRIVER_SIDE' },
    RETURN_WRONG_UNSCAN_NODE: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.returnWrongUnscanNode', field: 'returnWrongUnscanNodeLcCount', level1: 'WAREHOUSE_SIDE' },
    NOT_PICKED_UP: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.notPickedUp', field: 'notPickedUpLcCount', level1: 'WAREHOUSE_SIDE' },
    RETURN_NOT_PUT_AWAY: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.returnNotPutAway', field: 'returnNotPutAwayLcCount', level1: 'WAREHOUSE_SIDE' },
    RETURN_ON_PUT_AWAY: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.returnOnPutAway', field: 'returnOnPutAwayLcCount', level1: 'WAREHOUSE_SIDE' },
    RETURN_UN_PUT_AWAY: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.returnUnPutAway', field: 'returnUnPutAwayLcCount', level1: 'WAREHOUSE_SIDE' },
    LOST_DAMAGED_WHS: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.lostDamagedWhs', field: 'lostDamagedWhsLcCount', level1: 'WAREHOUSE_SIDE' },
    OTHER: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.other', field: 'otherLcCount', level1: 'WAREHOUSE_SIDE' },
    HANDOVER_DIFF_NODE: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.handoverDiffNode', field: 'handoverDiffNodeLcCount', level1: 'HANDOVER_DIFF' },
    RETURN_PKG: { labelKey: 'wms.delivery.parcelSlaMonitoring.undeliveredLevel2.returnPkg', field: 'returnPkgLcCount', level1: 'RETURN' },
};

/** 取 L1 展示标签（i18n），供表头/饼图/Tag/Drawer 标题使用 */
export const getLevel1Label = (t: TFunction, l1: UndeliveredLevel1): string => t(LEVEL1_META[l1].labelKey);

/** 取 L2 展示标签（i18n），供表头/单元格/筛选选项/Drawer 标题使用 */
export const getLevel2Label = (t: TFunction, l2: UndeliveredLevel2): string => t(LEVEL2_META[l2].labelKey);

/**
 * 取 L2 标签用于「L1-L2」组合语境（如 Excel 表头 `司机侧-丢失破损`）。
 * L2 原始标签会带侧别后缀（如 `丢失破损(司机侧)`）以在无 L1 语境时消歧；组合语境下 L1 前缀已表达侧别，
 * 剥掉尾部半/全角括号内容以降噪。
 */
export const getLevel2LabelInGroup = (t: TFunction, l2: UndeliveredLevel2): string =>
    getLevel2Label(t, l2).replace(/\s*[（(][^（()）]*[)）]\s*$/, '');

/** PRD 表格中 12 子列按大类展开的顺序 */
export const LEVEL1_ORDER: UndeliveredLevel1[] = ['DRIVER_SIDE', 'WAREHOUSE_SIDE', 'HANDOVER_DIFF', 'RETURN'];

export const LEVEL2_ORDER: UndeliveredLevel2[] = [
    'OUT_FOR_DELIVERY_NODE',
    'FAILED_NOT_RETURNED_NODE',
    'LOST_DAMAGED_DRIVER',
    'RETURN_WRONG_UNSCAN_NODE',
    'NOT_PICKED_UP',
    'RETURN_NOT_PUT_AWAY',
    'RETURN_ON_PUT_AWAY',
    'RETURN_UN_PUT_AWAY',
    'LOST_DAMAGED_WHS',
    'OTHER',
    'HANDOVER_DIFF_NODE',
    'RETURN_PKG',
];

/** 一级 → 归属该一级的 12 子列顺序 */
export const LEVEL2_BY_LEVEL1: Record<UndeliveredLevel1, UndeliveredLevel2[]> = {
    DRIVER_SIDE: ['OUT_FOR_DELIVERY_NODE', 'FAILED_NOT_RETURNED_NODE', 'LOST_DAMAGED_DRIVER'],
    WAREHOUSE_SIDE: ['RETURN_WRONG_UNSCAN_NODE', 'NOT_PICKED_UP', 'RETURN_NOT_PUT_AWAY', 'RETURN_ON_PUT_AWAY', 'RETURN_UN_PUT_AWAY', 'LOST_DAMAGED_WHS', 'OTHER'],
    HANDOVER_DIFF: ['HANDOVER_DIFF_NODE'],
    RETURN: ['RETURN_PKG'],
};

/** 未妥投总数 = 4 大类之和；warehouseSide 已含 other，勿再叠加 */
export const undeliveredTotal = (row: UndeliveredAttributionRow | undefined | null): number => {
    if (!row) return 0;
    return (row.driverSideLcCount ?? 0)
        + (row.warehouseSideLcCount ?? 0)
        + (row.handoverDiffLcCount ?? 0)
        + (row.returnLcCount ?? 0);
};

/** 编码：饼图 4 大类 → 'v2:${L1}'；表内 12 子列 → 'v2:${L1}:${L2}' */
export const encodeUndeliveredAnomaly = (level1: UndeliveredLevel1, level2?: UndeliveredLevel2): string =>
    level2 ? `v2:${level1}:${level2}` : `v2:${level1}`;

/** 解码：只解析以 'v2:' 打头的 anomalyType，返回 null 表示非 v2 归因 */
export const decodeUndeliveredAnomaly = (anomalyType: string): { level1: UndeliveredLevel1; level2?: UndeliveredLevel2 } | null => {
    if (!anomalyType.startsWith('v2:')) return null;
    const parts = anomalyType.slice(3).split(':');
    const l1 = parts[0] as UndeliveredLevel1;
    if (!(l1 in LEVEL1_META)) return null;
    if (parts.length === 1) return { level1: l1 };
    const l2 = parts[1] as UndeliveredLevel2;
    if (!(l2 in LEVEL2_META)) return { level1: l1 };
    return { level1: l1, level2: l2 };
};
